/*
 * Shipping protection for Gottsmart (Horizon theme) — ported from Dangaroo.
 * - Default ON: added automatically (qty 1) as soon as the cart holds a real product.
 * - Customer can switch it off; that choice is remembered for the current cart only.
 * - Removed automatically when no real products are left; never more than qty 1.
 * - Watches every cart change (theme, Kaching Bundles, other apps) and keeps the cart in sync.
 * - Refreshes the Horizon cart sections directly (no cart event, so no add_to_cart
 *   analytics/pixel event is fired for the protection line).
 */
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const cfg = window.shippingProtectionConfig || {};
const VARIANT_ID = Number(cfg.variantId || 0);

if (VARIANT_ID && !window.__shippingProtectionLoaded) {
  window.__shippingProtectionLoaded = true;
  init();
}

function init() {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const OPT_OUT_KEY = (cfg.storageKey || 'sp') + '_opted_out_cart';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);

  let running = false;
  let rerun = false;
  let timer = null;
  let idleWaiters = [];

  /* ---------- storage (opt-out is tied to the cart token) ---------- */
  const cartKey = (cart) => String((cart && cart.token) || '').split('?')[0];
  const getOptOut = () => {
    try {
      return window.localStorage.getItem(OPT_OUT_KEY);
    } catch (e) {
      return null;
    }
  };
  const setOptOut = (value) => {
    try {
      if (value) window.localStorage.setItem(OPT_OUT_KEY, value);
      else window.localStorage.removeItem(OPT_OUT_KEY);
    } catch (e) {
      /* ignore */
    }
  };
  const isOptedOut = (cart) => {
    const stored = getOptOut();
    return !!stored && stored === cartKey(cart);
  };

  /* ---------- api ---------- */
  function api(path, body) {
    const options = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    } else {
      options.cache = 'no-store';
    }
    return nativeFetch(ROOT + path, options).then((res) => {
      if (!res.ok) throw new Error('Shipping protection request failed: ' + res.status);
      return res.json();
    });
  }

  function countLines(cart) {
    let protection = 0;
    (cart.items || []).forEach((item) => {
      if (Number(item.variant_id) === VARIANT_ID) protection += item.quantity;
    });
    return { protection, other: cart.item_count - protection };
  }

  /* ---------- ui ---------- */
  function setLoading(isLoading) {
    document.querySelectorAll('[data-shipping-protection]').forEach((el) => {
      el.classList.toggle('is-loading', isLoading);
    });
  }

  function syncToggles(checked) {
    document.querySelectorAll('.shipping-protection__toggle-input').forEach((input) => {
      input.checked = checked;
    });
  }

  function cartComponents() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'));
  }

  function sectionIds() {
    return Array.from(new Set(cartComponents().map((el) => el.dataset.sectionId)));
  }

  function refreshSections(sections) {
    cartComponents().forEach((el) => {
      const id = el.dataset.sectionId;
      const mode = el.hasAttribute('data-drawer') ? 'hydration' : 'full';
      try {
        sectionRenderer.abortRender(id);
        if (sections && sections[id]) {
          morphSection(id, sections[id], { mode });
        } else {
          sectionRenderer.renderSection(id, { cache: false, mode }).catch(() => {});
        }
      } catch (e) {
        console.warn(e);
      }
    });
  }

  function updateBubble(cart) {
    const counts = countLines(cart);
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(counts.other, false);
    });
  }

  /* ---------- core: make the cart match the desired state ---------- */
  function reconcile() {
    if (running) {
      rerun = true;
      return;
    }
    running = true;
    let finalCart = null;

    api('cart.js')
      .then((cart) => {
        const counts = countLines(cart);
        const wanted = counts.other > 0 && !isOptedOut(cart) ? 1 : 0;
        syncToggles(wanted === 1);
        finalCart = cart;

        if (counts.protection === wanted) return null;
        setLoading(true);

        // Ask Shopify to render the cart sections in the same request.
        const ids = sectionIds();
        const extra = ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {};
        if (counts.protection === 0) {
          return api('cart/add.js', Object.assign({ items: [{ id: VARIANT_ID, quantity: 1 }] }, extra));
        }
        const updates = {};
        updates[VARIANT_ID] = wanted;
        return api('cart/update.js', Object.assign({ updates }, extra));
      })
      .then((response) => {
        if (!response) return null;
        // cart/add.js returns the added items, not the cart: read the cart again.
        return api('cart.js').then((cart) => {
          finalCart = cart;
          refreshSections(response.sections);
          updateBubble(cart);
        });
      })
      .catch((error) => {
        console.warn(error);
      })
      .then(() => {
        if (finalCart) {
          const counts = countLines(finalCart);
          syncToggles(counts.other > 0 && !isOptedOut(finalCart));
        }
        setLoading(false);
        running = false;
        if (rerun) {
          rerun = false;
          reconcile();
          return;
        }
        if (!timer) flushIdle();
      });
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(
      () => {
        timer = null;
        reconcile();
      },
      typeof delay === 'number' ? delay : 250
    );
  }

  const isBusy = () => running || !!timer;

  function whenIdle() {
    if (!isBusy()) return Promise.resolve();
    return new Promise((resolve) => idleWaiters.push(resolve));
  }

  function flushIdle() {
    const waiters = idleWaiters;
    idleWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  /* ---------- watch cart changes from any source ---------- */
  window.fetch = function (input, init) {
    const promise = nativeFetch(input, init);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method === 'POST' && CART_MUTATION.test(url)) {
        promise.then(
          () => schedule(),
          () => {}
        );
      }
    } catch (e) {
      /* ignore */
    }
    return promise;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__spCartMutation = String(method).toUpperCase() === 'POST' && CART_MUTATION.test(String(url));
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__spCartMutation) {
      this.addEventListener('loadend', () => schedule());
    }
    return nativeSend.apply(this, arguments);
  };

  /* ---------- customer toggles ---------- */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input || !input.classList || !input.classList.contains('shipping-protection__toggle-input')) return;
    const wantsProtection = input.checked;
    setLoading(true);
    api('cart.js')
      .then((cart) => setOptOut(wantsProtection ? null : cartKey(cart)))
      .catch(() => {})
      .then(() => schedule(0));
  });

  /* ---------- never send the customer to checkout mid-update ---------- */
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target && event.target.closest && event.target.closest('button[name="checkout"]');
      if (!button || !isBusy()) return;
      event.preventDefault();
      event.stopPropagation();
      button.setAttribute('aria-disabled', 'true');
      whenIdle().then(() => {
        window.location.href = ROOT + 'checkout';
      });
    },
    true
  );

  /* ---------- initial sync on every page load ---------- */
  schedule(0);
}
